import { useCallback, useEffect, useMemo, useState } from "react";

import {
  faCalendarClock,
  faCube,
  faExclamationCircle,
  faRefresh,
  faSearch,
  faThumbTack,
  faTrashAlt,
} from "@fortawesome/pro-regular-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { isAxiosError } from "axios";
import { endOfToday, startOfToday, subDays } from "date-fns";
import { DateRange, SelectRangeEventHandler } from "react-day-picker";

import { DateRangePicker } from "src/base-components/DateRangePicker";
import { Divider } from "src/base-components/Divider";
import { FilterFormData } from "src/base-components/FilterPopover/types";
import {
  Cell,
  ColumnVisibilityDropdown,
  TableComp,
} from "src/base-components/Table";
import { TabData, Tabs } from "src/base-components/Tabs";
import {
  FilterMode,
  RelatedEventsFilterDropdown,
} from "src/cases/caseView/blocks/RelatedEventsFilterDropdown";
import { CaseBlock, CaseBlockProps } from "src/cases/caseView/CaseBlock";
import { useFilterModeUrl } from "src/cases/caseView/hooks/useFilterModeUrl";
import { PinButton } from "src/cases/common/PinButton";
import { RelatedBlockTabLabel } from "src/cases/common/RelatedBlockTabLabel";
import { useUpdateBlockConfig } from "src/cases/queries/cases";
import {
  CaseEnvironment,
  PinnedEvent,
  RelatedEventsCaseBlockConfigAttributes,
} from "src/clients/case-management";
import { EventConfigEnriched } from "src/clients/features-control";
import { EventFilterRequestEnvironmentEnum } from "src/clients/features-retrieve";
import { EmptyState } from "src/design-system/EmptyState";
import { IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { useEntitySchemas } from "src/entities/queries";
import { useEventEntityDisplayMap } from "src/entities/useEntityDisplayValues";
import { getTableStoreKey } from "src/eventsCatalogue/EventsCatalogue";
import {
  DeserializedEvent,
  eventSchemaKeys,
  eventsKeys,
  useEventSchemas,
  useEventsByIds,
  useEventsFilters,
  useFilteredEventsQuery,
} from "src/eventsCatalogue/queries";
import { getColumns } from "src/eventsCatalogue/tableConfig";
import { getEventIcon } from "src/eventsCatalogue/utils";
import { Environment } from "src/hooks/useEnvironment";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { CasePageParams } from "src/router/urls";
import { errorMessage } from "src/utils/stringUtils";
import { useTimeWindow } from "src/utils/timeWindow";
import { useParamsDecode } from "src/utils/useParamsDecode";

const DEFAULT_TIME_WINDOW_DAYS = 7;

const helper = createColumnHelper<DeserializedEvent>();

type RelatedEventsBlockProps = Omit<CaseBlockProps, "children"> & {
  data: RelatedEventsCaseBlockConfigAttributes;
  caseEnv?: CaseEnvironment;
};

type ErrorState = {
  data: unknown;
  status: number | undefined;
};

type EventTabContentProps = {
  schema: EventConfigEnriched;
  pinnedEvents: PinnedEvent[];
  onEventPinChange: (id: string, timestamp: string, pinned: boolean) => void;
  environment: Environment;
  events: DeserializedEvent[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  handleFetchNextPage: () => void;
  hasFilterCondition: boolean;
  filterError: ErrorState | undefined;
  isPending: boolean;
  clearFilters: () => void;
  showPinnedOnly: boolean;
  onResetFilter: () => void;
  pinnedEventsError?: ErrorState;
  onRetryPinnedEvents: () => void;
  onRetry: () => void;
  requestedPinnedCount: number;
};

const buildEventColumnsWithPinAction = (
  schema: EventConfigEnriched,
  entitiesSchemasData: ReturnType<typeof useEntitySchemas>["data"],
  pinnedEvents: PinnedEvent[],
  onEventPinChange: (id: string, timestamp: string, pinned: boolean) => void,
  environment: Environment,
  entityDisplayMap?: Record<string, string>,
) => {
  const baseColumns = getColumns(
    schema,
    environment,
    entitiesSchemasData,
    undefined,
    entityDisplayMap,
  );
  const columnsWithoutActions = baseColumns.filter(
    (col) => col.id !== "actions",
  );

  const pinColumn = helper.display({
    id: "pin",
    header: (info) => (
      <div className="flex h-8 items-center justify-center bg-white">
        <ColumnVisibilityDropdown min={3} table={info.table} />
      </div>
    ),
    size: 54,
    minSize: 54,
    maxSize: 54,
    meta: {
      sticky: "right",
    },
    cell: (info) => {
      const eventId = info.row.original.payload.id;
      const timestamp = info.row.original.payload.timestamp;
      const isPinned = pinnedEvents.some((e) => e.id === eventId);

      return (
        <Cell
          className="items-center justify-center group-hover:bg-gray-50"
          info={info}
        >
          <PinButton
            dataLoc={`event-${eventId}`}
            pinned={isPinned}
            onChange={(pinned) => onEventPinChange(eventId, timestamp, pinned)}
          />
        </Cell>
      );
    },
  });

  return [...columnsWithoutActions, pinColumn];
};

const EventTabContent: React.FC<EventTabContentProps> = ({
  schema,
  pinnedEvents,
  onEventPinChange,
  environment,
  isFetchingNextPage,
  handleFetchNextPage,
  hasFilterCondition,
  filterError,
  isPending,
  clearFilters,
  showPinnedOnly,
  onResetFilter,
  events,
  isLoading,
  pinnedEventsError,
  onRetryPinnedEvents,
  onRetry,
  requestedPinnedCount,
}) => {
  const eventType = schema.event_type;
  const displayName = schema.display_name_plural || schema.event_type;
  const { workspace } = useWorkspaceContext();
  const { data: entitiesSchemasData } = useEntitySchemas({
    baseUrl: workspace.base_url!,
  });

  const { displayMap: entityDisplayMap } = useEventEntityDisplayMap({
    eventSchema: schema,
    events,
    environment,
  });

  const memoizedColumns = useMemo(() => {
    if (!schema) return [];

    return buildEventColumnsWithPinAction(
      schema,
      entitiesSchemasData,
      pinnedEvents,
      onEventPinChange,
      environment,
      entityDisplayMap,
    );
  }, [
    schema,
    entitiesSchemasData,
    pinnedEvents,
    onEventPinChange,
    environment,
    entityDisplayMap,
  ]);

  const hasNoEvents = !isLoading && events.length === 0;

  // Show filter error if present (including 400 errors from POST request)
  if (
    filterError &&
    !showPinnedOnly &&
    (!isPending || filterError.status === 400)
  ) {
    return (
      <div className="flex h-full w-full items-center justify-center p-4">
        <EmptyState
          actionButton={{
            icon: faRefresh,
            label: "Retry",
            onClick: onRetry,
          }}
          dataLoc={`events-list-${eventType}-filter-error-state`}
          description={errorMessage(filterError.data)}
          headline="Error loading events"
          icon={faExclamationCircle}
          size="compact"
          variant="error"
        />
      </div>
    );
  }

  // Show pinned events fetch error
  if (showPinnedOnly && pinnedEventsError) {
    return (
      <div className="flex h-full w-full items-center justify-center p-4">
        <EmptyState
          actionButton={{
            icon: faRefresh,
            label: "Retry",
            onClick: onRetryPinnedEvents,
          }}
          dataLoc={`events-list-${eventType}-pinned-error-state`}
          description={errorMessage(pinnedEventsError.data)}
          headline="Error loading pinned events"
          icon={faExclamationCircle}
          size="compact"
          variant="error"
        />
      </div>
    );
  }

  // Show state when pinned events were deleted (IDs exist but API returned nothing)
  if (
    showPinnedOnly &&
    !isLoading &&
    !pinnedEventsError &&
    requestedPinnedCount > 0 &&
    events.length === 0
  ) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <EmptyState
          actionButton={{
            label: "Show all events",
            onClick: onResetFilter,
          }}
          dataLoc={`events-list-${eventType}-empty-state-pinned-deleted`}
          description={`Pinned ${displayName.toLowerCase()} could not be found. Try to adjust filtering time period.`}
          headline="Pinned events not found"
          icon={faThumbTack}
          size="compact"
        />
      </div>
    );
  }

  // Show empty state when "pinned only" is selected but no pinned events exist
  if (showPinnedOnly && pinnedEvents.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <EmptyState
          actionButton={{
            label: "Show all events",
            onClick: onResetFilter,
          }}
          dataLoc={`events-list-${eventType}-empty-state-no-pinned`}
          description={`There are no pinned ${displayName.toLowerCase()}`}
          headline="No pinned events"
          icon={faThumbTack}
          size="compact"
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col p-4">
      <div className="min-h-0 flex-1">
        <TableComp
          columns={memoizedColumns}
          data={events ?? []}
          dataLoc={`events-list-${eventType}-table`}
          frameClassName="h-full"
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          rowClassName="group hover:bg-gray-50"
          uiStoreKey={getTableStoreKey(`events-list-${eventType}`)}
          variant="compact"
          onScrolledToTheBottom={handleFetchNextPage}
        />
      </div>
      {hasNoEvents && hasFilterCondition && (
        <EmptyState
          actionButton={{
            label: "Clear filters",
            onClick: clearFilters,
            icon: faTrashAlt,
          }}
          dataLoc={`events-list-${eventType}-empty-state-filtered`}
          description="Try adjusting the filter to find what you are looking for"
          headline="No events found"
          icon={faSearch}
          size="compact"
        />
      )}
      {hasNoEvents && !hasFilterCondition && (
        <EmptyState
          dataLoc={`events-list-${eventType}-empty-state`}
          description="No events found for the selected time period"
          headline="No events"
          icon={faSearch}
          size="compact"
        />
      )}
    </div>
  );
};

export const RelatedEventsBlock: React.FC<RelatedEventsBlockProps> = ({
  data,
  caseEnv,
  ...props
}) => {
  const blockEntityId = data.entity.id ?? undefined;
  const { workspace } = useWorkspaceContext();
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const queryClient = useQueryClient();
  const env = caseEnv === CaseEnvironment.LIVE ? "live" : "sandbox";
  const updateBlockConfigMutation = useUpdateBlockConfig();

  const {
    data: eventTypes,
    isLoading: isLoadingEventTypes,
    isFetching: isRefreshing,
  } = useEventSchemas();

  // Filter event types based on configuration
  // If data.event_types is null/undefined, show all (current behavior)
  // If data.event_types is an array, only show those event types
  const filteredEventTypes = useMemo(() => {
    if (!eventTypes) return [];
    const configuredEventTypes = data.event_types;
    return configuredEventTypes
      ? eventTypes.filter((et) => configuredEventTypes.includes(et.event_type))
      : eventTypes;
  }, [eventTypes, data.event_types]);

  const [selectedTab, setSelectedTab] = useState<string>("");
  const [isCustomFiltersOpen, setIsCustomFiltersOpen] = useState(false);

  const { dateRangePickerValue, onDateRangePickerChange } = useTimeWindow(
    {
      from: subDays(startOfToday(), DEFAULT_TIME_WINDOW_DAYS),
      to: endOfToday(),
    },
    `events-list-block-time-window`,
  );

  const timeWindow = useTimeWindowFromDateRange(dateRangePickerValue);

  // URL-based filter mode management
  const { getFilterMode, setFilterMode, urlParamKey } = useFilterModeUrl({
    blockKey: props.blockKey,
    eventType: selectedTab,
  });
  const showPinnedOnly = getFilterMode(false) === "pinned";

  // Use the filter hook at parent level for the currently selected tab
  const {
    applyFilters,
    clearFilters,
    urlFilterParams,
    filterParams,
    hasFilterCondition,
  } = useEventsFilters({
    entityId: blockEntityId,
    eventType: selectedTab,
    endTimestamp: timeWindow?.to || "",
    environment: env as EventFilterRequestEnvironmentEnum,
    startTimestamp: timeWindow?.from || "",
  });

  const {
    events,
    isPending: isFilterPending,
    error: filterError,
    isRefetching: isRefetchingEvents,
    isFetchingNextPage,
    handleFetchNextPage,
    refetch,
  } = useFilteredEventsQuery({
    eventType: selectedTab,
    entityId: blockEntityId,
    environment: env as EventFilterRequestEnvironmentEnum,
    filterParams,
    enabled: !!blockEntityId && !!selectedTab && !showPinnedOnly,
  });

  const pinnedEventsWithTimestamp = useMemo(
    () =>
      data.pinned_events
        .filter((e) => e.event_type === selectedTab)
        .map((e) => ({
          id: e.id,
          timestamp: e.timestamp ?? timeWindow.to,
        })),
    [data.pinned_events, selectedTab, timeWindow.to],
  );

  // Fetch pinned events only when "show pinned only" filter is enabled
  const {
    data: pinnedEventsData,
    isLoading: isPinnedEventsLoading,
    error: pinnedEventsError,
    refetch: refetchPinnedEvents,
  } = useEventsByIds(
    {
      eventIds: pinnedEventsWithTimestamp,
      environment: env as EventFilterRequestEnvironmentEnum,
      timeWindow,
      eventType: selectedTab,
    },
    {
      enabled: !!blockEntityId && showPinnedOnly,
    },
  );

  const pinnedEventsErrorTransformed = useMemo(() => {
    if (!pinnedEventsError) return undefined;
    return {
      data: isAxiosError(pinnedEventsError)
        ? pinnedEventsError.response?.data
        : pinnedEventsError,
      status: isAxiosError(pinnedEventsError)
        ? pinnedEventsError.response?.status
        : undefined,
    };
  }, [pinnedEventsError]);

  useEffect(() => {
    if (filteredEventTypes.length > 0 && !selectedTab) {
      setSelectedTab(filteredEventTypes[0].event_type);
    }
  }, [filteredEventTypes, selectedTab]);

  const handleDateRangeChange: SelectRangeEventHandler = useCallback(
    (range: DateRange | undefined) => {
      if (range?.from && range?.to) {
        onDateRangePickerChange(range);
      }
    },
    [onDateRangePickerChange],
  );

  const handleFilterModeChange = useCallback(
    (mode: FilterMode) => {
      setFilterMode(mode);
      if (mode === "all") {
        clearFilters();
      }
    },
    [setFilterMode, clearFilters],
  );

  const handleApplyFilters = useCallback(
    (formData: FilterFormData) => {
      applyFilters(formData, dateRangePickerValue, [urlParamKey]);
    },
    [applyFilters, dateRangePickerValue, urlParamKey],
  );

  const handleRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: eventSchemaKeys.workspaceList(workspace.id),
    });
    if (blockEntityId && selectedTab) {
      queryClient.invalidateQueries({
        queryKey: eventsKeys.schemaEvents(
          workspace.id,
          selectedTab,
          blockEntityId,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: eventsKeys.eventsByIds(
          workspace.id,
          env,
          selectedTab,
          data.pinned_events
            .filter((e) => e.event_type === selectedTab)
            .map((e) => e.id),
          timeWindow,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: eventsKeys.filteredEvents(
          workspace.id,
          selectedTab,
          blockEntityId,
          env,
        ),
      });
    }
  };

  const handleEventPinChange = useCallback(
    (eventId: string, timestamp: string, pinned: boolean) => {
      if (!selectedTab) return;

      const currentPinnedEvents = data.pinned_events;
      const isAlreadyPinned = currentPinnedEvents.some((e) => e.id === eventId);

      let updatedEvents: PinnedEvent[];
      if (pinned) {
        updatedEvents = isAlreadyPinned
          ? currentPinnedEvents
          : [
              ...currentPinnedEvents,
              {
                id: eventId,
                event_type: selectedTab,
                timestamp,
              },
            ];
      } else {
        updatedEvents = currentPinnedEvents.filter((e) => e.id !== eventId);
      }

      updateBlockConfigMutation.mutate({
        caseId,
        blockKey: props.blockKey,
        attributes: {
          pinned_events: updatedEvents,
        },
      });
    },
    [
      data.pinned_events,
      selectedTab,
      caseId,
      props.blockKey,
      updateBlockConfigMutation,
    ],
  );

  const currentSchema = eventTypes?.find((e) => e.event_type === selectedTab);
  const isLoading = isFilterPending || isRefetchingEvents || !currentSchema;
  const selectedDisplayName = useMemo(() => {
    if (!eventTypes || !selectedTab) return "events";
    const eventType = eventTypes.find((e) => e.event_type === selectedTab);
    return eventType?.display_name_plural || eventType?.event_type || "events";
  }, [eventTypes, selectedTab]);

  const customButton = (
    <div className="ml-auto flex h-11 items-center">
      <Divider orientation="vertical" variant="dark" />
      <div className="p-1.5">
        <DateRangePicker
          disabled={isCustomFiltersOpen}
          suffixIcon={!isCustomFiltersOpen}
          value={dateRangePickerValue}
          borderless
          onChange={handleDateRangeChange}
        />
      </div>
      <Divider orientation="vertical" variant="dark" />
      <div className="p-1.5">
        <RelatedEventsFilterDropdown
          dateRangeValue={dateRangePickerValue}
          defaultValues={urlFilterParams?.formData}
          displayName={selectedDisplayName}
          eventType={selectedTab}
          filterMode={getFilterMode(hasFilterCondition)}
          hasFilterCondition={hasFilterCondition}
          hasPinnedEvents={
            data.pinned_events.filter((e) => e.event_type === selectedTab)
              .length > 0
          }
          schemaIcon={currentSchema ? getEventIcon(currentSchema) : undefined}
          onApply={handleApplyFilters}
          onCustomFiltersOpenChange={setIsCustomFiltersOpen}
          onDateRangeChange={handleDateRangeChange}
          onFilterModeChange={handleFilterModeChange}
        />
      </div>
      <Divider orientation="vertical" variant="dark" />
      <Tooltip delay={350} placement="bottom" title="Refresh" asChild>
        <div className="p-3.5">
          <IconButton
            dataLoc="events-refresh"
            icon={faRefresh}
            size="sm"
            spin={isRefreshing}
            onClick={handleRefresh}
          />
        </div>
      </Tooltip>
    </div>
  );

  const tabs: TabData[] = useMemo(() => {
    return filteredEventTypes.map((eventType) => {
      const filteredPinnedEvents = data.pinned_events.filter(
        (e) => e.event_type === eventType.event_type,
      );
      const isCurrentTab = eventType.event_type === selectedTab;

      return {
        key: eventType.event_type,
        label: (
          <RelatedBlockTabLabel pinnedCount={filteredPinnedEvents.length}>
            {eventType.display_name_plural || eventType.event_type}
          </RelatedBlockTabLabel>
        ),
        content: (
          <EventTabContent
            clearFilters={clearFilters}
            environment={env}
            events={(showPinnedOnly ? pinnedEventsData : events) ?? []}
            filterError={isCurrentTab ? filterError : undefined}
            handleFetchNextPage={handleFetchNextPage}
            hasFilterCondition={isCurrentTab ? hasFilterCondition : false}
            isFetchingNextPage={isCurrentTab ? isFetchingNextPage : false}
            isLoading={showPinnedOnly ? isPinnedEventsLoading : isLoading}
            isPending={isCurrentTab ? isFilterPending : false}
            pinnedEvents={filteredPinnedEvents}
            pinnedEventsError={
              isCurrentTab ? pinnedEventsErrorTransformed : undefined
            }
            requestedPinnedCount={filteredPinnedEvents.length}
            schema={eventType}
            showPinnedOnly={showPinnedOnly}
            onEventPinChange={handleEventPinChange}
            onResetFilter={() => setFilterMode("all")}
            onRetry={refetch}
            onRetryPinnedEvents={refetchPinnedEvents}
          />
        ),
      };
    });
  }, [
    filteredEventTypes,
    data.pinned_events,
    selectedTab,
    clearFilters,
    env,
    showPinnedOnly,
    pinnedEventsData,
    events,
    filterError,
    handleFetchNextPage,
    hasFilterCondition,
    isFetchingNextPage,
    isPinnedEventsLoading,
    isLoading,
    isFilterPending,
    handleEventPinChange,
    setFilterMode,
    pinnedEventsErrorTransformed,
    refetch,
    refetchPinnedEvents,
  ]);

  if (!blockEntityId) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="events-list-empty-state"
          description="This block currently has no linked entity."
          headline="No entity provided"
          icon={faCube}
          size="compact"
        />
      </CaseBlock>
    );
  }

  if (!eventTypes?.length && !isLoadingEventTypes) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="events-list-empty-state"
          description="There are no events found related to this entity yet."
          headline="No events found"
          icon={faCalendarClock}
          size="compact"
        />
      </CaseBlock>
    );
  }

  return (
    <CaseBlock
      {...props}
      isLoading={isLoadingEventTypes}
      noPaddings={true}
      noScroll={true}
    >
      <Tabs
        containerClassName="h-full flex flex-col"
        customButton={customButton}
        panelClassName="flex-1 min-h-0 overflow-hidden"
        panelsClassName="h-full flex flex-col"
        selectedKey={selectedTab}
        tabClassName="px-2 h-11 flex items-center whitespace-nowrap after:translate-y-0"
        tabListClassName="flex-shrink-0 flex items-center h-11 border-b border-gray-200 bg-white pl-2.5"
        tabs={tabs}
        onChange={setSelectedTab}
      />
    </CaseBlock>
  );
};

const useTimeWindowFromDateRange = (dateRange: DateRange | undefined) => {
  return useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) {
      return {
        from: subDays(startOfToday(), DEFAULT_TIME_WINDOW_DAYS).toISOString(),
        to: endOfToday().toISOString(),
      };
    }
    return {
      from: dateRange.from.toISOString(),
      to: dateRange.to.toISOString(),
    };
  }, [dateRange]);
};
